/* Presentation + local photo previews. Shopify's native product form owns cart submission. */
(() => {
  if (!customElements.get('mcc-pet-customizer')) {
    customElements.define('mcc-pet-customizer', class extends HTMLElement {
      connectedCallback() {
        if (document.readyState === 'loading') {
          document.addEventListener('DOMContentLoaded', () => this.init(), { once: true });
        } else this.init();
      }
      disconnectedCallback() {
        this.abort?.abort();
        this.urls?.forEach(url => URL.revokeObjectURL(url));
        this.initialized = false;
      }
      init() {
        if (this.initialized || !this.isConnected) return;
        this.initialized = true;
        this.abort = new AbortController();
        const signal = this.abort.signal;
        const strings = JSON.parse(this.querySelector('[data-customizer-strings]').textContent);
        const picker = this.querySelector('[data-photo-picker]');
        const trigger = this.querySelector('[data-upload-trigger]');
        const inputs = [...this.querySelectorAll('[data-photo-input]')];
        const previews = this.querySelector('[data-preview-list]');
        const status = this.querySelector('[data-upload-status]');
        const error = this.querySelector('[data-upload-error]');
        const form = document.getElementById(this.dataset.productFormId);
        if (form) form.enctype = 'multipart/form-data';
        this.urls = [];
        const entries = () => inputs.filter(input => input.files?.length).map(input => ({ input, file: input.files[0] }));
        const showError = message => {
          error.textContent = message;
          error.hidden = !message;
          if (message) trigger.setAttribute('aria-invalid', 'true');
          else trigger.removeAttribute('aria-invalid');
        };
        const render = () => {
          this.urls.forEach(url => URL.revokeObjectURL(url));
          this.urls = [];
          previews.replaceChildren();
          const selected = entries();
          previews.hidden = !selected.length;
          status.textContent = selected.length + ' / 5 ' + strings.added;
          selected.forEach(({input,file}) => {
            const figure = document.createElement('figure');
            figure.className = 'mcc-pet-customizer__preview-item';
            const image = document.createElement('img');
            const url = URL.createObjectURL(file);
            this.urls.push(url);
            image.src = url;
            image.alt = strings.preview + ' ' + file.name;
            image.className = 'mcc-pet-customizer__preview-image';
            const remove = document.createElement('button');
            remove.type = 'button';
            remove.className = 'mcc-pet-customizer__remove';
            remove.textContent = strings.remove;
            remove.setAttribute('aria-label', strings.remove + ' ' + file.name);
            remove.addEventListener('click', () => {
              input.value = '';
              showError('');
              render();
              trigger.focus();
            });
            figure.append(image, remove);
            previews.append(figure);
          });
        };
        trigger.addEventListener('click', () => {
          if (entries().length >= 5) { showError(strings.limit); return; }
          picker.click();
        }, {signal});
        picker.addEventListener('change', () => {
          const files = [...picker.files];
          if (!files.length) return;
          let message = '';
          for (const file of files) {
            if (!['image/jpeg','image/png','image/webp'].includes(file.type)) { message = strings.type; continue; }
            if (file.size > 10 * 1024 * 1024) { message = strings.size; continue; }
            const slot = inputs.find(input => !input.files?.length);
            if (!slot) { message = strings.limit; break; }
            const transfer = new DataTransfer();
            transfer.items.add(file);
            slot.files = transfer.files;
          }
          picker.value = '';
          render();
          showError(message);
        }, {signal});
        // Window capture runs before Horizon's document-level event delegation.
        const validateSubmission = event => {
          const invalid = [...this.querySelectorAll('input[type=text],select,textarea')].find(field => !field.checkValidity());
          if (invalid || !entries().length) {
            event.preventDefault();
            event.stopImmediatePropagation();
            if (invalid) { invalid.focus(); invalid.reportValidity(); }
            else { showError(strings.empty); trigger.focus(); }
          } else showError('');
        };
        window.addEventListener('submit', event => {
          // An input named "id" shadows form.id.
          if (event.target.getAttribute('id') === this.dataset.productFormId) validateSubmission(event);
        }, {capture:true,signal});
        window.addEventListener('click', event => {
          const button = event.target instanceof Element ? event.target.closest('button[type="submit"]') : null;
          if (button?.form?.getAttribute('id') === this.dataset.productFormId) validateSubmission(event);
        }, {capture:true,signal});
        render();
      }
    });
  }
  if (!customElements.get('mcc-personalize-bar')) {
    customElements.define('mcc-personalize-bar', class extends HTMLElement {
      connectedCallback() {
        if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => this.init(), {once:true});
        else this.init();
      }
      disconnectedCallback() { this.observer?.disconnect(); this.visibility?.disconnect(); this.abort?.abort(); this.initialized=false; }
      init() {
        if (this.initialized || !this.isConnected) return;
        this.initialized = true;
        this.abort = new AbortController();
        const root = document.getElementById('shopify-section-' + this.dataset.sectionId);
        if (!root) return;
        const link = this.querySelector('[data-personalize-link]');
        const customizer = root.querySelector('mcc-pet-customizer');
        if (customizer) link.href = '#' + customizer.id;
        link.addEventListener('click', event => {
          event.preventDefault();
          const target = root.querySelector('variant-picker');
          target?.scrollIntoView({behavior:matchMedia('(prefers-reduced-motion: reduce)').matches?'auto':'smooth',block:'start'});
          root.querySelector('variant-picker input:checked')?.focus({preventScroll:true});
        }, {signal:this.abort.signal});
        const nativePrice = root.querySelector('product-price');
        const syncPrice = () => {
          const price = nativePrice?.querySelector('.price__regular:not(.price__hidden) .price, .price__sale:not(.price__hidden) .price');
          if (price) this.querySelector('[data-sticky-price]').replaceChildren(...[...price.childNodes].map(node => node.cloneNode(true)));
        };
        syncPrice();
        if (nativePrice) { this.observer = new MutationObserver(syncPrice); this.observer.observe(nativePrice,{childList:true,subtree:true,characterData:true,attributes:true}); }
        const button = root.querySelector('.add-to-cart-button');
        if (button) {
          this.visibility = new IntersectionObserver(([entry]) => this.toggleAttribute('data-hidden',entry.isIntersecting),{threshold:0});
          this.visibility.observe(button);
        }
        this.setAttribute('data-ready','');
      }
    });
  }
})();
